import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import ReactSelect from 'react-select';
import { useUser } from '@clerk/clerk-react';
import {
  Download,
  Star,
  ArrowUp,
  Send,
  MapPin,
  Search,
  Globe,
  Share2,
  Calendar as CalendarIcon,
  FileText,
  MessageSquare,
  Image as ImageIcon,
  TrendingUp,
  RefreshCw
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { authSelector } from '../../store/slices/auth';
import {
  selectedClientSelector,
  setSelectedClient,
  clearSelectedClient
} from '../../store/slices/selectedClient';
import { clientsSelector, getClients } from '../../store/slices/clients';
import {
  API_CLIENTS_NAME,
  API_KEY,
  METRICS_SUMMARY_ENDPOINT,
  SEO_AGENTS_API
} from '../../utils/constants';
import { getGoogleProfileBeforeSnapshot } from '../../components/GoogleBusinessProfile/services/index';
import { accountManagers } from '../../utils/helpers';
import { red } from '@material-ui/core/colors';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const DATE_RANGE_OPTIONS = [
  { value: 'last-30-days', label: 'Last 30 Days' },
  { value: 'last-3-months', label: 'Last 3 Months' },
  { value: 'last-6-months', label: 'Last 6 Months' },
  { value: 'last-year', label: 'Last Year' }
];

const DATE_RANGE_TO_MONTHS = {
  'last-30-days': 1,
  'last-3-months': 3,
  'last-6-months': 6,
  'last-year': 12
};

const getMonthsFromRange = (range) => DATE_RANGE_TO_MONTHS[range] || 1;

const formatResponseRate = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return '0%';
  }

  if (numericValue <= 1) {
    return `${Math.round(numericValue * 100)}%`;
  }

  return `${Math.round(numericValue)}%`;
};

const formatScoreChange = (value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return '0';
  }

  return numericValue > 0 ? `${numericValue}` : `${numericValue}`;
};

const toDisplayDate = (value) => {
  if (!value) {
    return '-';
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }

  return parsedDate.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const sectionCardStyle = {
  borderRadius: '1rem',
  border: 'none',
  boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)'
};

const CircularScore = ({ value, suffix = '%' }) => {
  const numericValue = Number(value) || 0;
  const cappedValue = Math.min(100, Math.max(0, numericValue));
  const radius = 50;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (cappedValue / 100) * circumference;

  return (
    <div className="relative h-[132px] w-[132px] shrink-0">
      <svg className="h-full w-full -rotate-90" viewBox="0 0 120 120">
        <circle
          cx="60"
          cy="60"
          r={radius}
          fill="none"
          stroke="#e2e8f0"
          strokeWidth="9"
        />
        <circle
          cx="60"
          cy="60"
          r={radius}
          fill="none"
          stroke="#3766a8"
          strokeWidth="9"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className="transition-all duration-700"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-[30px] font-bold text-foreground leading-none">
          {numericValue}
          {suffix}
        </span>
      </div>
    </div>
  );
};

const dateRangeSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '56px',
    borderRadius: '0.75rem',
    borderColor: state.isFocused ? 'hsl(var(--primary))' : 'hsl(var(--border))',
    boxShadow: state.isFocused ? '0 0 0 1px hsl(var(--primary))' : 'none',
    backgroundColor: '#ffffff',
    cursor: 'pointer',
    paddingLeft: '0.25rem',
    paddingRight: '0.25rem',
    '&:hover': {
      borderColor: 'hsl(var(--primary))'
    }
  }),
  valueContainer: (provided) => ({
    ...provided,
    paddingLeft: '0.5rem',
    paddingRight: '0.25rem'
  }),
  singleValue: (provided) => ({
    ...provided,
    color: '#111827',
    fontSize: '1rem'
  }),
  indicatorSeparator: () => ({
    display: 'none'
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: '#94a3b8',
    paddingRight: '0.75rem',
    '&:hover': {
      color: '#94a3b8'
    }
  }),
  menu: (provided) => ({
    ...provided,
    borderRadius: '0.75rem',
    overflow: 'hidden',
    border: '1px solid hsl(var(--border))',
    boxShadow:
      '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
    marginTop: '0.5rem'
  }),
  menuList: (provided) => ({
    ...provided,
    paddingTop: '0.5rem',
    paddingBottom: '0.5rem',
    overflowX: 'hidden'
  }),
  option: (provided, state) => ({
    ...provided,
    margin: 0,
    borderRadius: '0.375rem',
    paddingLeft: '0.875rem',
    paddingRight: '0.875rem',
    backgroundColor: state.isSelected
      ? 'hsl(var(--accent))'
      : state.isFocused
      ? 'hsl(var(--accent))'
      : 'transparent',
    color: '#111827',
    cursor: 'pointer'
  }),
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 9999
  })
};

const Stat = ({ value, label, accent = 'default', icon: Icon }) => {
  const colorMap = {
    default: '#111827',
    primary: '#3766a8',
    green: '#059669',
    red: '#ef4444'
  };

  return (
    <div className="space-y-1">
      <div
        className="flex items-center gap-2 text-3xl font-bold"
        style={{ color: colorMap[accent] || colorMap.default }}>
        {Icon && <Icon className="h-6 w-6" />}
        <span>{value}</span>
      </div>
      <p className="text-sm text-muted-foreground font-medium">{label}</p>
    </div>
  );
};

const SectionCard = ({ icon: Icon, title, children, disabled = false }) => (
  <Card
    style={{
      ...sectionCardStyle,
      opacity: disabled ? 0.5 : 1,
      backgroundColor: disabled ? '#f5f5f5' : 'transparent',
      pointerEvents: disabled ? 'none' : 'auto'
    }}>
    <div className="px-5 sm:px-7 pt-5 pb-3">
      <div className="flex items-center gap-2.5">
        <div
          className={`h-9 w-9 rounded-xl flex items-center justify-center ${
            disabled ? 'bg-gray-200' : 'bg-primary/10'
          }`}>
          <Icon
            className={`h-5 w-5 ${disabled ? 'text-gray-400' : 'text-primary'}`}
          />
        </div>
        <h2
          className={`text-lg sm:text-2xl font-semibold ${
            disabled ? 'text-gray-400' : 'text-foreground'
          }`}>
          {title}
        </h2>
      </div>
    </div>
    <div className="px-5 sm:px-7 pb-6">{children}</div>
  </Card>
);

const Reports = () => {
  const reportContentRef = useRef(null);
  const history = useHistory();
  const dispatch = useDispatch();
  const { user } = useUser();
  const { selectedClient } = useSelector(selectedClientSelector);
  const { customerList } = useSelector(clientsSelector);
  const { userInfo } = useSelector(authSelector);

  const [dateRange, setDateRange] = useState('last-30-days');
  const [metricsSummary, setMetricsSummary] = useState(null);
  const [isLoadingSummary, setIsLoadingSummary] = useState(false);
  const [isMobileView, setIsMobileView] = useState(
    () => window.innerWidth < 640
  );
  const [seoScanData, setSeoScanData] = useState(null);
  const [isLoadingSeo, setIsLoadingSeo] = useState(false);
  const [gbpSnapshotData, setGbpSnapshotData] = useState(null);

  const userEmail = user?.primaryEmailAddress?.emailAddress || '';
  const isSuperAdmin =
    userEmail.endsWith('@adlerlocal.com') ||
    userEmail.endsWith('@samxlabs.com') ||
    userEmail.endsWith('@adlersocial.com');

  useEffect(() => {
    const handleResize = () => setIsMobileView(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!customerList?.length && userInfo?.accessToken) {
      dispatch(getClients());
    }
  }, [dispatch, customerList?.length, userInfo?.accessToken]);

  useEffect(() => {
    const clientId = selectedClient?.id;

    if (!clientId) {
      setMetricsSummary(null);
      return;
    }

    const fetchSummary = async () => {
      setIsLoadingSummary(true);

      try {
        const response = await axios.post(
          METRICS_SUMMARY_ENDPOINT,
          {
            clientId,
            months: getMonthsFromRange(dateRange)
          },
          {
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );

        setMetricsSummary(response?.data || null);
      } catch (error) {
        console.error('Error fetching metrics summary:', error);
        setMetricsSummary(null);
      } finally {
        setIsLoadingSummary(false);
      }
    };

    fetchSummary();
  }, [selectedClient?.id, dateRange]);

  const getCustomerList = useMemo(() => {
    if (!customerList?.length) {
      return [];
    }

    return customerList.map((customer) => ({
      ...customer,
      label: customer.clientName,
      value: customer.clientName
    }));
  }, [customerList]);

  // Reset snapshot when client changes
  useEffect(() => {
    setGbpSnapshotData(null);
  }, [selectedClient?.id]);

  // Fetch GBP snapshot for authoritative optimization percentage
  useEffect(() => {
    if (!selectedClient?.id) return;

    const fetchSnapshot = async () => {
      const snapshot = await getGoogleProfileBeforeSnapshot(selectedClient.id);
      setGbpSnapshotData(snapshot);
    };

    fetchSnapshot();
  }, [selectedClient?.id]);

  const reportsData = useMemo(() => {
    const metrics = metricsSummary?.metrics;
    const reviews = metrics?.reviews;
    const gbp = metrics?.googleBusinessProfile;
    const seo = metrics?.seo;
    const ranking = metrics?.localRanking;
    const social = metrics?.social;
    const accountOwnerId = selectedClient?.accountOwner;
    const accountManagerData = accountManagers.find(
      (manager) => manager.id === String(accountOwnerId)
    );

    return {
      reviewsWins: {
        newReviews: reviews?.newReviews ?? 0,
        averageRating: reviews?.averageRating ?? 0,
        fiveStarReviews: reviews?.fiveStarReviews ?? 0,
        respondedReviews: reviews?.reviewsResponded ?? 0,
        responseRate: formatResponseRate(reviews?.responseRate),
        smsRequests: reviews?.smsRequestsSent ?? 0,
        highlightedReview: reviews?.lastReviewMessage || 'No reviews yet',
        highlightedAuthor: 'Metrics summary'
      },
      profileHealth: {
        optimization: (gbpSnapshotData?.afterPercentage != null
          ? Math.round(gbpSnapshotData.afterPercentage)
          : Number(gbp?.optimizationScore || 0)
        ).toFixed(0),
        gbpPosts: gbp?.postsPublished ?? 0,
        photosAdded: gbp?.photosAdded ?? 0
      },
      seoScan: {
        score: seoScanData?.overallScore ?? seo?.score ?? 0,
        passed: seoScanData?.summary?.passed ?? seo?.passed ?? 0,
        warnings: seoScanData?.summary?.warnings ?? seo?.warnings ?? 0,
        lastScan: seoScanData?.completedAt
          ? toDisplayDate(seoScanData.completedAt)
          : seo?.lastScanDate
          ? toDisplayDate(seo.lastScanDate)
          : '-'
      },
      rankingMomentum: {
        keywordsTracked: ranking?.keywordsTracked ?? 0,
        rankedTop3: ranking?.top3Ranked ?? 0,
        improvedPeriod: ranking?.improvedKeywords ?? 0,
        keywords: (ranking?.topKeywords || []).map((item) => ({
          keyword: item.keyword,
          before: item.previousRank,
          after: item.currentRank
        }))
      },
      socialContent: {
        postsPublished: social?.postsPublished ?? 0,
        upcomingPosts: social?.scheduledPosts ?? 0
      },
      accountManager: {
        name: accountManagerData?.full_name || '-',
        email: accountManagerData?.email || ''
      }
    };
  }, [
    metricsSummary,
    selectedClient?.accountOwner,
    seoScanData,
    gbpSnapshotData
  ]);

  const handleSeoRefresh = async () => {
    const clientId = selectedClient?.id;
    const url = selectedClient?.mergeFields?.['~Website'];

    if (!clientId || !url) return;

    setIsLoadingSeo(true);
    try {
      // Step 1: Start the audit
      const auditStartResponse = await axios.post(
        `https://iw0nmalze1.execute-api.us-east-2.amazonaws.com/dev/audit`,
        {
          url,
          maxPages: 50,
          version: 'v3',
          forceRescan: true,
          lookupOnly: false
        },
        { headers: { 'Content-Type': 'application/json' } }
      );

      const { reportId } = auditStartResponse?.data || {};

      if (!reportId) {
        throw new Error('No reportId received from audit start');
      }

      // Step 2: Poll for completion
      let auditData = null;
      let attempts = 0;
      const maxAttempts = 60; // Max 5 minutes (5 seconds * 60)

      while (attempts < maxAttempts) {
        const statusResponse = await axios.get(
          `https://iw0nmalze1.execute-api.us-east-2.amazonaws.com/dev/audit-v2/job/${reportId}`,
          { headers: { 'Content-Type': 'application/json' } }
        );

        const statusData = statusResponse?.data;

        if (statusData?.status === 'completed') {
          auditData = statusData;
          break;
        }

        // Wait 5 seconds before next poll
        await new Promise((resolve) => setTimeout(resolve, 5000));
        attempts++;
      }

      if (!auditData) {
        throw new Error('Audit timed out or failed');
      }

      setSeoScanData(auditData);

      // Step 3: Patch the client with new SEO data
      if (auditData) {
        await axios.post(
          `https://dincsdvg9l.execute-api.us-east-1.amazonaws.com/metrics/seo/patch`,
          {
            clientId,
            score: auditData.overallScore,
            passed: auditData.summary?.passed,
            warnings: auditData.summary?.warnings,
            lastScanDate: auditData.createdAt,
            reportId: auditData.reportId
          },
          { headers: { 'Content-Type': 'application/json' } }
        );
      }
    } catch (error) {
      console.error('Error fetching SEO audit:', error);
    } finally {
      setIsLoadingSeo(false);
    }
  };

  const handleChangeClient = async (selectedOption) => {
    if (!selectedOption) return;

    dispatch(clearSelectedClient());

    try {
      const response = await axios.get(
        `${API_CLIENTS_NAME}${selectedOption.clientName}`,
        {
          headers: {
            'X-API-KEY': API_KEY,
            Authorization: `bearer ${userInfo.accessToken}`
          }
        }
      );

      if (response?.data) {
        dispatch(setSelectedClient(response.data));
      } else {
        dispatch(setSelectedClient(selectedOption));
      }
    } catch (error) {
      console.error('Error fetching complete client data:', error);
      dispatch(setSelectedClient(selectedOption));
    }

    history.push('/reports');
  };

  const exportToPDF = async () => {
    if (!reportContentRef.current) return;

    try {
      const element = reportContentRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pdfWidth - 20; // 10mm margins
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 10; // 10mm top margin

      // Add image pages
      pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight - 20;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight + 10;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
        heightLeft -= pdfHeight - 20;
      }

      // Generate filename with client name and date range
      const clientName = selectedClient?.clientName || 'Report';
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `${clientName}_Report_${timestamp}.pdf`;

      pdf.save(filename);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  return (
    <div style={{ minHeight: '100vh' }}>
      <main
        ref={reportContentRef}
        className="overflow-x-hidden"
        style={{
          padding: isMobileView ? '0.75rem' : '2.5rem',
          paddingBottom: isMobileView ? '1rem' : '1.5rem'
        }}>
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 sm:gap-4 mb-6 sm:mb-8">
          <div className="flex items-center gap-4 min-w-0">
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-foreground break-words">
                Reports, {selectedClient ? selectedClient.clientName : ''}
              </h1>
              <p className="text-sm sm:text-base text-muted-foreground mt-1">
                Overview of your performance by category
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 w-full lg:w-auto">
            {isSuperAdmin && (
              <div className="hidden lg:block relative w-72">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground z-10" />
                <ReactSelect
                  placeholder="Search"
                  onChange={(client) => handleChangeClient(client)}
                  options={getCustomerList}
                  hideSelectedOptions={true}
                  controlShouldRenderValue={false}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  components={{
                    DropdownIndicator: () => null,
                    IndicatorSeparator: () => null
                  }}
                  openMenuOnClick={false}
                  openMenuOnFocus={false}
                  styles={{
                    control: (provided, state) => ({
                      ...provided,
                      borderRadius: '0.75rem',
                      borderWidth: '1px',
                      borderColor: state.isFocused
                        ? 'hsl(var(--primary))'
                        : 'hsl(var(--border))',
                      boxShadow: state.isFocused
                        ? '0 0 0 1px hsl(var(--primary))'
                        : 'none',
                      backgroundColor: '#ffffff',
                      paddingTop: '10px',
                      paddingBottom: '9px',
                      paddingLeft: '2.5rem',
                      fontSize: '1rem',
                      '&:hover': {
                        borderColor: 'hsl(var(--primary))'
                      }
                    }),
                    placeholder: (provided) => ({
                      ...provided,
                      color: 'hsl(var(--muted-foreground))',
                      fontSize: '1rem'
                    }),
                    input: (provided) => ({
                      ...provided,
                      fontSize: '1rem',
                      color: 'hsl(var(--foreground))'
                    }),
                    menu: (provided) => ({
                      ...provided,
                      borderRadius: '0.75rem',
                      overflow: 'hidden',
                      zIndex: 9999,
                      boxShadow:
                        '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)'
                    }),
                    menuPortal: (provided) => ({
                      ...provided,
                      zIndex: 9999
                    }),
                    option: (provided, state) => ({
                      ...provided,
                      backgroundColor: state.isFocused ? '#f1f5f9' : 'white',
                      color: state.isFocused ? '#1e293b' : '#64748b',
                      cursor: 'pointer',
                      padding: '12px 16px',
                      fontSize: '1rem'
                    })
                  }}
                />
              </div>
            )}

            <div className="w-full sm:w-[210px]">
              <ReactSelect
                value={
                  DATE_RANGE_OPTIONS.find(
                    (option) => option.value === dateRange
                  ) || DATE_RANGE_OPTIONS[1]
                }
                onChange={(option) =>
                  setDateRange(option?.value || 'last-30-days')
                }
                options={DATE_RANGE_OPTIONS}
                isSearchable={false}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={dateRangeSelectStyles}
              />
            </div>

            <Button
              onClick={exportToPDF}
              className="h-12 sm:h-14 px-5 sm:px-7 sm:min-w-[204px] text-sm sm:text-base font-semibold rounded-xl gap-2 shadow-md hover:shadow-lg transition-all"
              style={{
                backgroundColor: '#3766a8',
                color: 'white'
              }}>
              <Download className="h-5 w-5" />
              Export to PDF
            </Button>
          </div>
        </div>

        <div className="space-y-4 sm:space-y-8">
          <SectionCard icon={Star} title="Reviews Wins">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-5 sm:gap-6">
              <Stat
                value={reportsData.reviewsWins.newReviews}
                label="New reviews this period"
                accent="green"
                icon={ArrowUp}
              />
              <Stat
                value={reportsData.reviewsWins.averageRating}
                label="Average star rating"
                accent="primary"
              />
              <Stat
                value={reportsData.reviewsWins.fiveStarReviews}
                label="5-star reviews"
              />
              <Stat
                value={reportsData.reviewsWins.respondedReviews}
                label="Reviews responded to"
              />
              <Stat
                value={reportsData.reviewsWins.responseRate}
                label="Response rate"
                accent="green"
              />
              <Stat
                value={reportsData.reviewsWins.smsRequests}
                label="SMS review requests sent"
                accent="green"
                icon={Send}
              />
            </div>

            <div
              className="mt-6 p-4 sm:p-5 rounded-xl bg-muted/40"
              style={{ borderLeft: '4px solid #3766a8' }}>
              <div className="flex items-center gap-1 mb-2">
                {Array.from({ length: 5 }).map((_, index) => (
                  <Star
                    key={`review-star-${index}`}
                    className="h-4 w-4"
                    style={{ fill: '#fbbf24', color: '#fbbf24' }}
                  />
                ))}
              </div>
              <p className="text-sm sm:text-base italic text-foreground leading-relaxed">
                {reportsData.reviewsWins.highlightedReview}
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                - {reportsData.reviewsWins.highlightedAuthor}
              </p>
            </div>
          </SectionCard>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-8">
            <SectionCard icon={MapPin} title="Google Business Profile Health">
              <div className="flex flex-col sm:flex-row items-center gap-5 sm:gap-6">
                <div className="flex flex-col items-center gap-2 shrink-0">
                  <CircularScore
                    value={reportsData.profileHealth.optimization}
                  />
                  <p className="text-sm text-muted-foreground font-medium">
                    Optimization
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4 flex-1 w-full sm:border-l sm:border-border sm:pl-6">
                  <Stat
                    value={reportsData.profileHealth.gbpPosts}
                    label="GBP posts published"
                  />
                  <Stat
                    value={reportsData.profileHealth.photosAdded}
                    label="Photos added"
                    accent="green"
                    icon={ArrowUp}
                  />
                </div>
              </div>
            </SectionCard>

            <SectionCard icon={Globe} title="Website SEO Scan">
              <div className="relative">
                {reportsData.seoScan.score > 0 &&
                  reportsData.seoScan.lastScan !== '' && (
                    <button
                      onClick={handleSeoRefresh}
                      disabled={isLoadingSeo}
                      className="absolute -top-3 -right-3 h-8 w-8 rounded-full bg-muted border border-border flex items-center justify-center hover:bg-muted/80 transition-colors z-10 disabled:cursor-not-allowed">
                      <RefreshCw
                        className={`h-4 w-4 text-muted-foreground ${
                          isLoadingSeo ? 'animate-spin' : ''
                        }`}
                      />
                    </button>
                  )}
                <div className="flex flex-col sm:flex-row items-center gap-5 sm:gap-6">
                  <div className="flex flex-col items-center gap-2 shrink-0">
                    <CircularScore
                      value={reportsData.seoScan.score}
                      suffix=""
                    />
                    <p className="text-sm text-muted-foreground font-medium">
                      SEO Score
                    </p>
                  </div>

                  <div className="flex-1 w-full space-y-3 sm:border-l sm:border-border sm:pl-6">
                    <div className="grid grid-cols-2 gap-4">
                      <Stat
                        value={reportsData.seoScan.passed}
                        label="Passed"
                        accent="green"
                      />
                      <Stat
                        value={reportsData.seoScan.warnings}
                        label="Warnings"
                        accent="red"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Last scan: {reportsData.seoScan.lastScan}
                    </p>
                  </div>
                </div>
              </div>
            </SectionCard>
          </div>

          <SectionCard icon={Share2} title="Social & Content Activity">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                  <ImageIcon className="h-5 w-5 text-primary" />
                </div>
                <Stat
                  value={reportsData.socialContent.postsPublished}
                  label="Posts published this period"
                />
              </div>

              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                  <CalendarIcon className="h-5 w-5 text-primary" />
                </div>
                <Stat
                  value={reportsData.socialContent.upcomingPosts}
                  label="Upcoming scheduled posts"
                />
              </div>
            </div>
          </SectionCard>

          <Card
            style={{
              ...sectionCardStyle,
              backgroundColor: '#0f1116',
              color: 'white'
            }}>
            <div className="p-5 sm:p-7 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-full flex items-center justify-center bg-white/10">
                  <MessageSquare className="h-5 w-5" />
                </div>

                <div>
                  <p className="text-xs uppercase tracking-wide text-white/70">
                    Your Account Manager
                  </p>
                  <p className="text-base sm:text-lg font-semibold">
                    {reportsData.accountManager.name}
                  </p>
                  {reportsData.accountManager.email ? (
                    <a
                      href={`mailto:${reportsData.accountManager.email}`}
                      className="text-sm text-white/80 hover:text-white underline">
                      {reportsData.accountManager.email}
                    </a>
                  ) : (
                    <p className="text-sm text-white/70">Email not available</p>
                  )}
                </div>
              </div>

              <Button
                onClick={exportToPDF}
                className="h-12 sm:h-14 px-5 sm:px-6 text-sm sm:text-base font-semibold rounded-xl gap-2 w-full sm:w-auto"
                style={{
                  backgroundColor: 'white',
                  color: '#111827'
                }}>
                <FileText className="h-5 w-5" />
                Export to PDF
              </Button>
            </div>
          </Card>
        </div>

        {isLoadingSummary && (
          <p className="mt-4 text-sm text-muted-foreground">
            Loading report metrics...
          </p>
        )}
      </main>
    </div>
  );
};

export default Reports;
